import { ScrollTracker } from '../../common/utilities/ScrollTracker';
import { Module } from '../Module';
import { IStackedElement, StackedElementsService } from '../services/StackedElementsService';

export interface IPopoverScope extends ng.IScope {
  visible: boolean;
  arrow: string;
}

export const ScrollingTolerance = 10; // amount of scrolling in px before an open popover is closed.

/**
 * Popover directive
 *
 * Adds overlays of content to any element for housing secondary information.
 * (very similar to Bootstrap's Popover)
 *
 * Usage:
 *
 *  div(tb-popover="'popoverTemplate.html'")
 *
 * An optional 'tb-popover-class' attribute can be used to specify a class to apply to
 * the popover container.
 *
 * The overlay's scope will inherit from the target element's scope.
 * Also, a 'visible' boolean property is available on the overlay's scope. This
 * is useful for lazy-loading the overlay content e.g. see ContentItemPopoverCtrl.ts.
 *
 */

let Id = 0;
Module.instance().directive('tbPopover', [
  '$window',
  '$compile',
  '$timeout',
  '$parse',
  ($window: ng.IWindowService, $compile: ng.ICompileService,
   $timeout: ng.ITimeoutService, $parse: ng.IParseService,) => {
    const stackedElementsService = StackedElementsService.instance;
    return {
      restrict: 'A',
      link: (scope: ng.IScope, element: JQuery, attributes: ng.IAttributes) => {

        let DisplayPopoverDelay = 600; // milliseconds
        const RemovePopoverDelay = 50; // milliseconds
        let PreferOpenLeft = false;
        let MarginRight = 0;
        let MarginLeft = 0;
        const templateUrl = attributes['tbPopover'];
        let popoverStackedElement: IStackedElement;
        let displayPopoverTimeout: ng.IPromise<any>;
        let removePopoverTimeout: ng.IPromise<any>;
        let scrollTracker: ScrollTracker;
        let popover: JQuery;
        let popoverScope: IPopoverScope;

        if ($parse(attributes['tbPopoverSuppress'])(scope)) {
          return;
        }

        const scrollElementEventNamespace = '.popover_events_' + Id++;
        const scrollElement = element.parents('.tb-scroll');

        createAndCompilePopover();
        displayPopoverOnMouseEnter();
        removePopoverOnMouseLeave();

        function createAndCompilePopover() {
          popover = $('<div>').addClass('tb-padded-wide')
            .append($('<div tb-arrow="arrow">').addClass('tb-popover')
              .append($('<div>').attr('ng-include', templateUrl)));

          if (attributes['tbPopoverClass']) {
            popover.addClass(attributes['tbPopoverClass']);
          }

          if (attributes['tbPopoverDelay']) {
            DisplayPopoverDelay = parseInt(attributes['tbPopoverDelay'], 10);
          }

          if (attributes['tbPopoverPreferOpenLeft']) {
            PreferOpenLeft = true;
          }

          if (attributes['tbPopoverMarginRight']) {
            MarginRight = parseInt(attributes['tbPopoverMarginRight'], 10);
          }
          if (attributes['tbPopoverMarginLeft']) {
            MarginLeft = parseInt(attributes['tbPopoverMarginLeft'], 10);
          }

          if (attributes['tbPopoverHide']) {
            scope.$watch(attributes['tbPopoverHide'], (hide: boolean) => {
              popover.css({ display: hide ? 'none' : 'block' });
            });
          }

          popoverScope = <IPopoverScope>scope.$new();
          popoverScope.visible = false;
          $compile(popover)(popoverScope);
        }

        function displayPopoverOnMouseEnter() {
          element.on('mouseenter', () => {
            displayPopoverTimeout = $timeout(buildPopoverStackedElement, DisplayPopoverDelay);
            waitForScrollingToStopBeforeOpeningPopover();
            keepPopoverOpenWhenMousedOver();
          });
        }

        function removePopoverOnMouseLeave() {
          element.on('mouseleave', () => {
            $timeout.cancel(displayPopoverTimeout);
            removePopoverTimeout = $timeout(removePopover, RemovePopoverDelay);
          });
        }

        function buildPopoverStackedElement() {
          popoverStackedElement = stackedElementsService.addStackedElement({
            contentElement: popover,
            getContentElementPosition: () => {
              const popoverWidth = popover.outerWidth();
              const windowWidth = angular.element($window).width();
              const elementWidth = element.outerWidth();
              const elementOffset = element.offset();
              const openLeftLeft = elementOffset.left - popoverWidth;
              const openRightLeft = elementOffset.left + elementWidth;
              const openRightRight = openRightLeft + popoverWidth;
              let openLeft = PreferOpenLeft;

              if (openLeft) {
                if (openLeftLeft < 0 && openRightRight <= windowWidth) {
                  openLeft = false;
                }
              } else {
                if (openRightRight > windowWidth && openLeftLeft >= 0) {
                  openLeft = true;
                }
              }

              popoverScope.arrow = openLeft ? 'right' : 'left';
              popover.addClass('tb-popover-' + popoverScope.arrow);

              // TODO: a more sophisticated positioning function to account for
              // top and bottom edges of window.

              return { left: openLeft ? openLeftLeft : openRightLeft, top: elementOffset.top };
            }
          });
          popoverScope.visible = true;
          scrollTracker = new ScrollTracker(scrollElement, (distanceScrolled: number) => {
            scope.$apply(() => {
              if (distanceScrolled >= ScrollingTolerance && popoverScope.visible) {
                removePopover();
              }
            });
          });

        }

        function waitForScrollingToStopBeforeOpeningPopover() {
          scrollElement.on('scroll' + scrollElementEventNamespace, () => {
            if (popoverScope.visible) {
              return;
            }

            // attempt to cancel the display popover timeout. If it was cancelled, restart it.
            if ($timeout.cancel(displayPopoverTimeout)) {
              displayPopoverTimeout = $timeout(buildPopoverStackedElement, DisplayPopoverDelay);
            }
          });
        }

        function keepPopoverOpenWhenMousedOver() {
          popover
            .on('mouseenter.popover_events', () => {
              $timeout.cancel(removePopoverTimeout);
            })
            .on('mouseleave.popover_events', () => {
              scope.$apply(removePopover);
            });
        }

        function removePopover() {
          if (popoverStackedElement) {
            scrollElement.off(scrollElementEventNamespace);
            stackedElementsService.removeStackedElement(popoverStackedElement);
            popoverStackedElement = null;
          }
          if (popover) {
            popover.off('.popover_events');
          }
          popoverScope.visible = false;
          popover.detach();

          if (scrollTracker) {
            scrollTracker.destroy();
          }
        }

        scope.$on('$destroy', () => {
          popoverScope.$destroy();
          removePopover();
          $timeout.cancel(displayPopoverTimeout);
          $timeout.cancel(removePopoverTimeout);
        });
      }
    };
  }
]);

Module.instance().directive('tbArrow', () => {
  return {
    restrict: 'A',
    link: (scope: ng.IScope, element: JQuery, attributes: ng.IAttributes) => {

      const popoverArrow = $('<div></div>');
      element.append(popoverArrow);

      scope.$watch(attributes['tbArrow'], (arrow: string) => {
        popoverArrow.removeClass();
        if (arrow === 'right') {
          popoverArrow.addClass('tb-modal-arrow-right');
        } else {
          popoverArrow.addClass('tb-modal-arrow-left');
        }
      });
    }
  };
});
